import React, { FC } from 'react';
import { Route, useLocation } from 'react-router';
import { CAMPAIGN_PRODUCTS, COLLECTION_NAVIGATION_V2, useFeature } from '../../shared/utils/togglz';
import { ProductGroupOverview } from './productgroups/overview/ProductGroupOverview';
import { ProductGroupDetail } from './productgroups/detail/ProductGroupDetail';
import { SortingView } from './productgroups/sorting/SortingView';
import { useEnvironment } from '../../shared/components/EnvironmentContext';
import { ENVIRONMENT_DEMO } from '../../constants';
import { YourCampaignsPage } from './campaigns/YourCampaignsPage';
import { CheckLoanFormat } from '../../shared/components/CheckLoanFormat';
import { ProductSelectionProvider } from '../../shared/product-selection/ProductSelectionContext';
import { useLoanFormat } from '../../app-store/loanFormat/useLoanFormat';
import { LOANFORMAT_ALL_OBJECT, LOANFORMAT_EPRESS_OBJECT } from '../../shared/loanFormat/constants';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';
import Utils from '../../shared/utils';
import { ProductGroupPartnerFallback } from './productgroups/overview/ProductGroupPartnerFallback';
import { Navigate, Routes } from 'react-router-dom';

const MarketingRouteSwitch: FC = () => {
    const { pathname, search } = useLocation();
    const isCollectionNavigationV2Enabled = useFeature(COLLECTION_NAVIGATION_V2);
    const campaignsEnabled = useFeature(CAMPAIGN_PRODUCTS);
    const authentication = useAuthentication();
    const inPartnerContext = Utils.isInPartnerContext(authentication);
    // CatMan is not available in the demo environment
    const libraryManagedProductGroupsEnabled = useEnvironment() !== ENVIRONMENT_DEMO;
    const {
        loanFormat: { urlKey: currentLoanFormat }
    } = useLoanFormat();

    if (!campaignsEnabled && !libraryManagedProductGroupsEnabled) {
        return <Navigate to="/" replace />;
    }

    return (
        <Routes>
            <Route
                index
                element={
                    campaignsEnabled ? (
                        <Navigate to={{ pathname: 'campaigns', search }} replace />
                    ) : (
                        <Navigate to={{ pathname: 'productgroups', search }} replace />
                    )
                }
            />
            <Route path="productgroups">
                <Route
                    index
                    element={
                        libraryManagedProductGroupsEnabled ? (
                            isCollectionNavigationV2Enabled ? (
                                <Navigate
                                    to={{ pathname: pathname.replace('/marketing', '/merchandising'), search }}
                                    replace
                                />
                            ) : inPartnerContext ? (
                                <ProductGroupPartnerFallback />
                            ) : (
                                <ProductGroupOverview />
                            )
                        ) : (
                            <Navigate to="/" replace />
                        )
                    }
                />
                <Route path=":productGroupId">
                    <Route
                        index
                        element={
                            libraryManagedProductGroupsEnabled ? (
                                isCollectionNavigationV2Enabled ? (
                                    <Navigate
                                        to={{ pathname: pathname.replace('/marketing', '/merchandising'), search }}
                                        replace
                                    />
                                ) : inPartnerContext ? (
                                    <Navigate to=".." replace />
                                ) : (
                                    <ProductSelectionProvider>
                                        <ProductGroupDetail />
                                    </ProductSelectionProvider>
                                )
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                    <Route
                        path="sorting"
                        element={
                            libraryManagedProductGroupsEnabled ? (
                                isCollectionNavigationV2Enabled ? (
                                    <Navigate
                                        to={{ pathname: pathname.replace('/marketing', '/merchandising'), search }}
                                        replace
                                    />
                                ) : inPartnerContext ? (
                                    <Navigate to={`../..`} replace />
                                ) : (
                                    <SortingView />
                                )
                            ) : (
                                <Navigate to="/" replace />
                            )
                        }
                    />
                </Route>
            </Route>
            <Route path="campaigns">
                <Route
                    index
                    element={
                        campaignsEnabled ? (
                            <Navigate
                                to={{
                                    pathname:
                                        currentLoanFormat === LOANFORMAT_EPRESS_OBJECT.urlKey
                                            ? LOANFORMAT_ALL_OBJECT.urlKey
                                            : currentLoanFormat,
                                    search
                                }}
                                replace
                            />
                        ) : (
                            <Navigate to="/" replace />
                        )
                    }
                />
                <Route
                    path=":loanFormat"
                    element={
                        campaignsEnabled ? (
                            isCollectionNavigationV2Enabled ? (
                                <Navigate
                                    to={{ pathname: pathname.replace('/marketing', '/collectionmgmt'), search }}
                                    replace
                                />
                            ) : (
                                <CheckLoanFormat>
                                    <YourCampaignsPage />
                                </CheckLoanFormat>
                            )
                        ) : (
                            <Navigate to="/" replace />
                        )
                    }
                />
            </Route>
        </Routes>
    );
};

export default MarketingRouteSwitch;
